import { useId, type ReactNode } from "react";
import { StatusBadge, type StatusLevel } from "./StatusBadge.tsx";

/**
 * Trace waterfall (design 12.2 Traces screen, task 11.11): the spans of one trace drawn as time
 * bars, offset by their start and sized by their duration, indented by nesting depth.
 *
 * Trace-level labels (design 7.4 / 12.2): `inferred` (correlation fell back to decision_id/agent/
 * time because no `traceparent` header could flow) and `partial` (allow-path spans were sampled out
 * while a deny/error span was kept). They are INDEPENDENT: both may show at once and NEITHER takes
 * precedence. They render as two separate badges, not one combined state.
 *
 * Accessibility (design 12.4): the waterfall is a semantic ordered list of spans, each row carrying
 * its name, service, status (icon + text via `StatusBadge`), offset and duration AS TEXT — the bar
 * is a decorative, `aria-hidden` overlay positioned from the same numbers. So the trace is fully
 * readable without seeing the bars; the list IS the data-table alternative. A span's own
 * `inferred`/`partial` flags (a single mis-correlated span) show as inline badges on its row.
 *
 * It is data-driven and screen-agnostic: the Traces screen maps its fetched trace into these props;
 * this component computes layout and renders, nothing more.
 */

export interface WaterfallSpan {
  /** Stable id for the span. */
  readonly id: string;
  /** Human span name, e.g. "POST /gateway/decision". */
  readonly name: string;
  /** The service the span belongs to, e.g. "gateway". */
  readonly service?: string;
  /** Start offset from the trace start, in milliseconds (>= 0). */
  readonly startMs: number;
  /** Duration in milliseconds (>= 0). A zero-duration span renders as a marker. */
  readonly durationMs: number;
  /** Nesting depth (0 = root); drives indentation. */
  readonly depth?: number;
  /** Worst status of the span, shown as an icon+text badge. */
  readonly status?: StatusLevel;
  /** This individual span was correlated by inference, not a propagated header. */
  readonly inferred?: boolean;
  /** This individual span is known to be missing children (sampled out). */
  readonly partial?: boolean;
}

export interface WaterfallProps {
  /** Accessible name for the trace, e.g. the root span name or trace id. */
  readonly title: string;
  /** The spans, in display order (callers usually pre-order by startMs then depth). */
  readonly spans: readonly WaterfallSpan[];
  /**
   * The total trace span in ms used to scale the bars. When omitted it is derived from the spans
   * (max end). Pass it when the screen knows the true trace window.
   */
  readonly totalMs?: number;
  /** The whole trace was correlated by inference (no traceparent could flow). */
  readonly inferred?: boolean;
  /** The whole trace is missing spans that were sampled out. */
  readonly partial?: boolean;
  /** Shown when there are no spans (design 12.3 honest empty state). */
  readonly emptyMessage?: string;
  /** Format a millisecond duration/offset for display. Defaults to `123 ms` / `1.23 s`. */
  readonly formatMs?: (ms: number) => string;
}

function defaultFormatMs(ms: number): string {
  if (!Number.isFinite(ms)) return "n/a";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

/** The scale denominator: the explicit total, or the largest span end, or 1 to avoid /0. */
function resolveTotal(spans: readonly WaterfallSpan[], totalMs: number | undefined): number {
  if (totalMs !== undefined && totalMs > 0) return totalMs;
  let max = 0;
  for (const s of spans) {
    const end = s.startMs + s.durationMs;
    if (end > max) max = end;
  }
  return max > 0 ? max : 1;
}

export function Waterfall(props: WaterfallProps): ReactNode {
  const labelId = useId();
  const formatMs = props.formatMs ?? defaultFormatMs;
  const total = resolveTotal(props.spans, props.totalMs);
  const isEmpty = props.spans.length === 0;

  return (
    <section className="waterfall" aria-labelledby={labelId}>
      <header className="waterfall__header">
        <h3 id={labelId} className="waterfall__title">
          {props.title}
        </h3>
        {/* Trace-level labels: independent badges, neither takes precedence (design 7.4). */}
        <span className="waterfall__labels">
          {props.inferred === true && <TraceLabel kind="inferred" />}
          {props.partial === true && <TraceLabel kind="partial" />}
        </span>
      </header>

      {isEmpty ? (
        <div className="chart__state" data-kind="empty">
          <strong>No spans</strong>
          <span>{props.emptyMessage ?? "This trace has no spans in range."}</span>
        </div>
      ) : (
        <ol className="waterfall__list">
          {props.spans.map((span) => {
            const depth = span.depth ?? 0;
            const leftPct = Math.max(0, Math.min(100, (span.startMs / total) * 100));
            const widthPct = Math.max(0.5, Math.min(100 - leftPct, (span.durationMs / total) * 100));
            return (
              <li key={span.id} className="waterfall__row" data-depth={depth}>
                <div className="waterfall__meta" style={{ paddingLeft: `${depth * 12}px` }}>
                  <span className="waterfall__name">{span.name}</span>
                  {span.service !== undefined && (
                    <span className="waterfall__service">{span.service}</span>
                  )}
                  {span.status !== undefined && (
                    <StatusBadge level={span.status} size="sm" />
                  )}
                  {span.inferred === true && <TraceLabel kind="inferred" />}
                  {span.partial === true && <TraceLabel kind="partial" />}
                </div>
                <div className="waterfall__track">
                  {/* The bar is decorative; the numbers to its right carry the data as text. */}
                  <span
                    className="waterfall__bar"
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    aria-hidden="true"
                  />
                </div>
                <span className="waterfall__timing">
                  {formatMs(span.startMs)} + {formatMs(span.durationMs)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/**
 * A trace label badge. `inferred` and `partial` are neutral informational labels (grey/neutral,
 * never a status colour) with icon + text, so they are legible without colour and never read as a
 * health state.
 */
function TraceLabel(props: { kind: "inferred" | "partial" }): ReactNode {
  const text = props.kind === "inferred" ? "inferred" : "partial";
  const glyph = props.kind === "inferred" ? "≈" : "⋯";
  const title =
    props.kind === "inferred"
      ? "Inferred: correlated without a propagated trace header"
      : "Partial: some spans were sampled out";
  return (
    <span className="trace-label" data-kind={props.kind} title={title}>
      <span className="trace-label__glyph" aria-hidden="true">
        {glyph}
      </span>
      <span>{text}</span>
    </span>
  );
}
